import { detectQuoteStyle, normalizeToQuoteStyle } from '../../util/normalizeQuoteStyle';
import { semanticDiffToChanges } from '../transforms/layered-diffs';
import type { Change } from '../transforms/layered-diffs';
export interface BreakChange {
  breakType: 'add-newline' | 'delete-newline' | 'retain-newline';
}
export type ChangeWithoutCount = Omit<Change, 'count'>;
export type AlineChange = BreakChange | ChangeWithoutCount;

const aiAssistantQueries = {
  /**
   * Takes an array of changes and breaks them into batches by new line character.
   */
  batchChangesByNewLine: (changes: ChangeWithoutCount[]): AlineChange[] => {
    return changes.flatMap<AlineChange>(change => {
      const textBatches = change.value.split('\n');
      const splitChanges = textBatches.flatMap((textPart, index): AlineChange[] => {
        const isLast = index === textBatches.length - 1;
        if (isLast) return textPart ? [{ ...change, value: textPart }] : [];

        // Multiple \n characters can result in textPart === ''.
        const breakChange: BreakChange = {
          breakType: change.added
            ? 'add-newline'
            : change.removed
              ? 'delete-newline'
              : 'retain-newline',
        };
        return textPart ? [{ ...change, value: textPart }, breakChange] : [breakChange];
      });

      return splitChanges;
    });
  },

  /**
   * Takes two strings and returns an array of AlineChanges.
   */
  getAlineChanges: (oldText: string, newText: string): ChangeWithoutCount[] => {
    const normalizedNew = aiAssistantQueries.normalizeQuoteStyleUsingStyleFrom(newText, oldText);
    return semanticDiffToChanges(oldText, normalizedNew);
  },

  /**
   * Type guard to determine if change is BreakChange.
   */
  getIsBreakChange: (change: AlineChange): change is BreakChange => {
    return (change as BreakChange).breakType !== undefined;
  },

  /**
   * Normalize AI-generated quote style to match the original document's quote style.
   * This prevents quote style differences from showing as content changes in diffs.
   */
  normalizeQuoteStyleUsingStyleFrom: (textToNormalize: string, referenceText: string): string => {
    const style = detectQuoteStyle(referenceText);
    return normalizeToQuoteStyle(textToNormalize, style);
  },
};

export default aiAssistantQueries;
